import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import FirestoreService from '../assets/js/firestore_instance';
import { motion, AnimatePresence } from 'framer-motion';
import { quantum } from 'ldrs'
import Confetti from 'react-confetti';
import useEnv from "../hooks/useEnv";



quantum.register()

const Confirmation = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = FirestoreService.getFirestore();
  const envUrl = useEnv();
  
  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [confettiActive, setConfettiActive] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  
  // Loading messages for animation
  const loadingMessages = [
    "Validating your subscription...",
    "Setting up your organization...",
    "Integrating your groups...",
    "Preparing your dashboard...",
    "Almost there...",
    "Finalizing setup..."
  ];

  const AnimatedText = ({ text }) => (
      <motion.div
        key={text}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 1 }}
        className="w-[500px] text-center"
      >
        <h1 className="text-2xl text-white font-bold">{text}</h1>
      </motion.div>
    );

  // Message rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => 
        (prevIndex + 1) % loadingMessages.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const authCheck = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/sign-in');
        return;
      }

      try {
        const docRef = doc(db, "admin_users", user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          await getOrganization(docSnap.data().organizationId[0]);
        }
      } catch (error) {
        console.error("Error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    });

    return () => authCheck();
  }, [auth, db, navigate]);

  useEffect(() => {
    if (organization) {
      validatePayment(auth.currentUser.uid);
    }
  }, [organization]);

  const getOrganization = async (orgId) => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (orgDoc.exists()) {
        setOrganization(orgDoc.data());
      } else {
        throw new Error("Organization not found");
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      setError(error.message);
    }
  };

  useEffect(() => {
    if (!loading && paymentSuccess) {
      setConfettiActive(true);
    } else {
      setConfettiActive(false);
    }
  
    return () => {
      setConfettiActive(false);
    };
  }, [loading, paymentSuccess]);

  const validatePayment = async (uid) => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (!sessionId) {
      setError("No session ID found");
      return;
    }

    try {
      // Verify payment
      const paymentResponse = await fetch(`${envUrl}/verifyStripeSession`, { //prod
      // const paymentResponse = await fetch('http://127.0.0.1:5001/joltchat/us-central1/verifyStripeSession', { //dev

        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, session_id: sessionId })
      });
      
      const paymentData = await paymentResponse.json();
      
      if (paymentData.status === 'succeeded') {
        setPaymentSuccess(true);
        // update org subscription status
        console.log(organization);
        await setDoc(doc(db, 'organizations', organization.organizationId), { subscription: 'founders' }, { merge: true });

        if (organization.orgType === "joltchat") {
          /// Add the current user as an admin on JoltChat if they exist
          try {
            // Get user doc
            const userDocRef = doc(db, "users", uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              
              // Safely initialize and update the admin organizations array
              const newOrgAdmins = [...(userData.isOrgAdmin || [])];
              if (!newOrgAdmins.includes(organization.organizationId)) {
                newOrgAdmins.push(organization.organizationId);
              }
              
              // Update user document
              await updateDoc(userDocRef, {
                isOrgAdmin: newOrgAdmins
              });
              
              console.log("User added as organization admin successfully");
            } else {
              // Handle case where user document doesn't exist
              console.warn(`User document for UID ${uid} not found in users collection`);
            }
          } catch (error) {
            console.error("Error updating user admin status:", error);
            // track this error in analytics
          }

          return;
        }

        // Merge staged groups
        const mergeResponse = await fetch(`${envUrl}/mergeStagedGroups`, { //prod
        // const mergeResponse = await fetch('http://127.0.0.1:5001/joltchat/us-central1/mergeStagedGroups', { //dev
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid })
        });
        
        await mergeResponse.json();
        // navigate('/dashboard');
      } else {
        throw new Error('Payment validation failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    }
  };

  return (
    <div className='min-h-screen min-w-screen p-8 dashboard-element-bg'>
      <div className='dashboard-main-color rounded-3xl w-full p-8 mt-20'>
        <div className="flex flex-col">
          <div className="border-b border-gray-700 pb-4 mb-6">
            <motion.h1 
              className="text-3xl font-bold text-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {organization?.organizationName || 'Validating Subscription...'}
            </motion.h1>
          </div>
          
          <div className="dashboard-element-bg rounded-lg p-8">
            <AnimatePresence mode="wait">
              {paymentSuccess ? (
                <motion.div
                key="success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.5 }}
                className="text-center text-white"
              >
                <h1 className="text-4xl font-bold mb-8">Congratulations! You're in!</h1>
                
                <motion.svg
                  className="mx-auto"
                  width="150"
                  height="150"
                  viewBox="0 0 150 150"
                >
                  <motion.circle
                    cx="75"
                    cy="75"
                    r="70"
                    stroke="#22c55e"
                    strokeWidth="8"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1.1 }}
                    transition={{ duration: 1.8, delay: 0.5 }}
                  />
                  <motion.path
                    d="M 45 75 L 65 95 L 105 55"
                    stroke="#22c55e"
                    strokeWidth="8"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  />
                </motion.svg>
                <div className="flex justify-center w-full">
                  <motion.button
                    onClick={() => 
                      organization?.orgType !== "joltchat" 
                        ? navigate('/finish-setup') 
                        : navigate('/setup-admin', { state: { organization } })
                    }
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.5,
                      delay: 2 
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="mt-14 mb-4 px-8 py-5 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full font-bold text-xl shadow-lg hover:shadow-xl transition-shadow flex flex-row items-center gap-2"
                  >
                    <span>Finish your setup</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </motion.button>
                </div>
              </motion.div>
              ) : error ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-center"
                >
                  <p>Error: {error}</p>
                  <button 
                    onClick={() => navigate('/staged-groups')}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
                  >
                    Return to Dashboard
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center justify-center"
                >
                  <div className="text-xl text-white mb-8">
                    <AnimatePresence mode="wait">
                      <AnimatedText text={loadingMessages[currentMessageIndex]} />
                    </AnimatePresence>
                  </div>
                  <l-quantum
                    size="50"
                    speed="1.75"
                    color="#ff7969" 
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {confettiActive && <Confetti />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;